// src/pages/auth/SecurityQuestionLoginPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Key, User, ArrowLeft, Loader2, Link2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useDispatch, useSelector } from 'react-redux';
import { fetchQuestions } from '../../store/slices/securityQuestionSlice';
import securityQuestionApi from 'api/securityQuestionApi';

const SecurityQuestionLoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { questions, isLoading: qLoading, error: qError } = useSelector(
    (state) => state.securityQuestion
  );


  const [employeeCode, setEmployeeCode] = useState('');
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [verified, setVerified] = useState(false);
  const [resetLink, setResetLink] = useState('');

  // Load questions
  useEffect(() => {
    dispatch(fetchQuestions());
  }, [dispatch]);

  useEffect(() => {
    if (qError) toast.error(qError);
  }, [qError]);

  // Validation
  const validate = () => {
    const e = {};
    if (!employeeCode.trim()) e.employeeCode = 'Employee code is required';
    questions.forEach((q) => {
      if (!answers[q.id]?.trim()) e[`answer_${q.id}`] = 'Answer required';
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const answersArray = Object.entries(answers).map(([qid, ans]) => [qid, ans]);

      const response = await securityQuestionApi.verify(employeeCode, answersArray);

      if (response.status === 'User verified') {
        toast.success('Verification successful!');
        setVerified(true);
        setResetLink(response.password_reset_link);

        // Clear form
        setEmployeeCode('');
        setAnswers({});
        setErrors({});
      }
    } catch (err) {
      console.log(err);
      const msg =
        err.response?.data?.error ||
        err.response?.data?.answers?.[0] ||
        err.error ||
        'Verification failed.';
      toast.error(msg);
      setErrors({ general: msg });
    } finally {
      setSubmitting(false);
    }
  };

  // Loading UI
  if (qLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading questions…</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center  p-4 bg-[#2a3646]">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8 animate-fade-in">
          <h1 className="text-3xl font-bold font-['Space_Grotesk'] mb-2 text-white">
            Security Verification
          </h1>
          <p className="text-muted-foreground text-white">
            Answer all security questions to reset the password
          </p>
        </div>

        <Card className="shadow-xl animate-slide-in">
          <CardHeader>
            <CardTitle className="text-2xl">Verify Your Identity</CardTitle>
            <CardDescription>
              Employee code + answer to every question
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Employee Code */}
              <div className="space-y-2">
                <Label htmlFor="employeeCode">Employee Code</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="employeeCode"
                    placeholder="e.g., EMP001"
                    value={employeeCode}
                    onChange={(e) => setEmployeeCode(e.target.value)}
                    className={cn('pl-10', errors.employeeCode && 'border-destructive')}
                    disabled={verified}
                  />
                </div>
                {errors.employeeCode && (
                  <p className="text-sm text-destructive">{errors.employeeCode}</p>
                )}
              </div>

              {/* One input per question */}
              {questions.map((q) => (
                <div key={q.id} className="space-y-2">
                  <Label>{q.question}</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Your answer"
                      value={answers[q.id] || ''}
                      onChange={(e) =>
                        setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))
                      }
                      className={cn(
                        'pl-10',
                        errors[`answer_${q.id}`] && 'border-destructive'
                      )}
                      disabled={verified}
                    />
                  </div>
                  {errors[`answer_${q.id}`] && (
                    <p className="text-sm text-destructive">{errors[`answer_${q.id}`]}</p>
                  )}
                </div>
              ))}

              {/* General error */}
              {errors.general && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <p className="text-sm text-destructive">{errors.general}</p>
                </div>
              )}

              {/* Submit Button */}
              {!verified && (
                <Button
                  type="submit"
                  className="w-full h-12 bg-[#2a3646]"
                  disabled={submitting || qLoading}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying…
                    </>
                  ) : (
                    'Verify & Continue'
                  )}
                </Button>
              )}

              {/* Reset Link Button */}
              {verified && resetLink && (
                <Button
                  type="button"
                  className="w-full h-12"
                  variant="default"
                  onClick={() => window.open(resetLink, '_blank', 'noopener,noreferrer')}
                >
                  <Link2 className="mr-2 h-4 w-4" />
                  Open Password Reset Link
                </Button>
              )}

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SecurityQuestionLoginPage;